// src/wiring/manifest/auditTelemetryPack.enterprise.ts
//
// Enterprise tier capability.
// - Observability and audit scaffolding (no payload retention)
// - Emits structured telemetry for wiring/contract health
// - Safe to install in authority app runtime; WP embed may use a cut-down variant.
//
// Notes:
// - Listens to:
//   - synktribe:contract:violation (guard/report layer)
//   - synktribe:wiring:ready (capability pass summary)
// - Uses only summaries (no raw payload retention)
//

import type { CapabilityManifest } from "@/contracts/capabilities/CapabilityManifest";
import { report } from "@/wiring/telemetry/report";

const CONTRACT_VIOLATION_EVENT = "synktribe:contract:violation" as const;
const WIRING_READY_EVENT = "synktribe:wiring:ready" as const;

/**
 * Canonical export name used by src/wiring/manifest/index.ts
 * (keeps registry imports stable).
 */
export const AuditTelemetryPackEnterpriseManifest: CapabilityManifest = {
  id: "synktribe.auditTelemetry",
  version: "1.0.0",
  safety: "transformer",
  tier: "enterprise",
  provides: ["telemetry.audit", "telemetry.tracing", "telemetry.contractGuard"],

  install() {
    const w = window as any;

    // Idempotency guard
    if (w.__synk_capabilities?.auditTelemetryInstalled) {
      report("capability.warn", {
        id: "synktribe.auditTelemetry",
        reason: "already_installed",
      });
      return () => {};
    }
    w.__synk_capabilities = w.__synk_capabilities || {};
    w.__synk_capabilities.auditTelemetryInstalled = true;

    let violationCount = 0;

    const onViolation = (e: Event) => {
      violationCount++;

      // Only store summaries (no raw payload)
      const d = (e as CustomEvent).detail as
        | {
            event?: string;
            where?: string;
            reason?: string;
            schema?: string;
            severity?: "info" | "warning" | "critical";
          }
        | undefined;

      report("capability.metric", {
        id: "synktribe.auditTelemetry",
        metric: "contract.violation.count",
        value: violationCount,

        // safe summaries:
        event: d?.event ?? "unknown",
        where: d?.where ?? "unknown",
        reason: d?.reason ?? "unknown",
        schema: d?.schema ?? "unknown",
        severity: d?.severity ?? "warning",
      });
    };

    const onWiringReady = (e: Event) => {
      // Align with your SynkWiringReadyDetailV1 shape but remain permissive.
      const d = (e as CustomEvent).detail as
        | {
            v?: number;
            at?: number;
            installed?: Array<{ id: string } | string>;
            tier?: string;
            buildId?: string;
            manifestChecksum?: string;
            env?: string;
          }
        | undefined;

      const installedCount = Array.isArray(d?.installed) ? d!.installed!.length : 0;

      report("capability.metric", {
        id: "synktribe.auditTelemetry",
        metric: "wiring.ready",
        value: 1,

        installedCount,
        tier: d?.tier ?? "unknown",
        buildId: d?.buildId ?? "unknown",
        env: d?.env ?? "unknown",
        manifestChecksum: d?.manifestChecksum ?? "unknown",
      });
    };

    window.addEventListener(CONTRACT_VIOLATION_EVENT, onViolation as EventListener);
    window.addEventListener(WIRING_READY_EVENT, onWiringReady as EventListener);

    // Optional: mark enterprise telemetry enabled (for UI overlays)
    w.__synk_observability = w.__synk_observability || {};
    w.__synk_observability.enterprise = true;

    report("capability.installed", {
      id: "synktribe.auditTelemetry",
      tier: "enterprise",
      safety: "transformer",
    });

    return () => {
      try {
        window.removeEventListener(CONTRACT_VIOLATION_EVENT, onViolation as EventListener);
        window.removeEventListener(WIRING_READY_EVENT, onWiringReady as EventListener);

        if (w.__synk_observability) {
          delete w.__synk_observability.enterprise;
        }
        if (w.__synk_capabilities) {
          delete w.__synk_capabilities.auditTelemetryInstalled;
        }

        report("capability.uninstalled", {
          id: "synktribe.auditTelemetry",
        });
      } catch {
        // never throw on uninstall
      }
    };
  },
};

/**
 * Back-compat alias (in case anything imported the old name).
 */
export const AuditTelemetryEnterpriseManifest = AuditTelemetryPackEnterpriseManifest;
